<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Button, PageHeader, Section, SegmentedControl, Select, useToast } from '@drama/ui'
import { Plus, RefreshCw } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import {
  fetchModelCapabilityCatalog,
  saveCapabilityModel,
  setCapabilityRoute,
} from '../../api/model-capabilities.js'
import { MODEL_CAPABILITIES } from './model-capability.js'
import CapabilityModelModal from './CapabilityModelModal.vue'
import TtsVoiceCatalog from './TtsVoiceCatalog.vue'
import CapabilityModelTable from './model/CapabilityModelTable.vue'

const { t } = useI18n()
const toast = useToast()
const route = useRoute()

const selectedCapability = ref('text')
const selectedProviderCapabilityId = ref('')
const catalog = ref(null)
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const modelModalOpen = ref(false)
const editingModel = ref(null)
const capabilityValues = MODEL_CAPABILITIES

const capabilityOptions = computed(() => capabilityValues.map(value => ({
  value,
  label: t(`visualGameAdmin.llm.capabilities.types.${value}`),
})))
const currentCapability = computed(() => catalog.value[selectedCapability.value])
const providersById = computed(() => new Map(catalog.value.providers.map(row => [row.id, row])))
const modelProviderFilterOptions = computed(() => {
  const providerCapabilityIds = new Set(currentCapability.value.models.map(row => row.provider_capability_id))
  return [
    { value: '', label: t('visualGameAdmin.llm.capabilities.filters.allProviders') },
    ...currentCapability.value.provider_capabilities
      .filter(row => providerCapabilityIds.has(row.id))
      .map(row => ({
        value: row.id,
        label: `${providerForCapability(row).display_name} · ${row.code}`,
      })),
  ]
})
const currentModels = computed(() => {
  if (selectedProviderCapabilityId.value === '') return currentCapability.value.models
  return currentCapability.value.models.filter(
    row => row.provider_capability_id === selectedProviderCapabilityId.value,
  )
})
const canAddModel = computed(() => currentCapability.value.provider_capabilities.length > 0)
onMounted(async () => {
  await loadCatalog()
  if (route.query.capability === undefined) return
  if (typeof route.query.capability !== 'string' || !capabilityValues.includes(route.query.capability)) {
    throw new Error('Model capability route: capability is invalid')
  }
  if (typeof route.query.provider_capability_id !== 'string' || !/^\d+$/.test(route.query.provider_capability_id)) {
    throw new Error('Model capability route: provider_capability_id is invalid')
  }
  selectedCapability.value = route.query.capability
  await nextTick()
  selectedProviderCapabilityId.value = Number(route.query.provider_capability_id)
})

watch(selectedCapability, () => {
  selectedProviderCapabilityId.value = ''
})

async function loadCatalog() {
  loading.value = true
  error.value = ''
  try {
    catalog.value = await fetchModelCapabilityCatalog()
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function openNewModel() {
  editingModel.value = null
  modelModalOpen.value = true
}

function providerForCapability(providerCapability) {
  return providersById.value.get(providerCapability.provider_id)
}

function openModel(row) {
  editingModel.value = row
  modelModalOpen.value = true
}

function modelSaved(nextCatalog) {
  catalog.value = nextCatalog
  toast.success(t('visualGameAdmin.llm.capabilities.saved'))
}

async function setModelEnabled(row, enabled) {
  saving.value = true
  try {
    catalog.value = await saveCapabilityModel(selectedCapability.value, {
      model: row.model,
      provider_capability_id: row.provider_capability_id,
      pricing: row.pricing,
      params: row.params,
      enabled,
      // 向量模型这一列必填。原样回传，服务端校验它和库里一致（维度不可改）。
      ...(selectedCapability.value === 'embedding' ? { dimensions: row.dimensions } : {}),
    })
    toast.success(t('visualGameAdmin.llm.capabilities.saved'))
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

async function setRoute(row) {
  saving.value = true
  try {
    catalog.value = await setCapabilityRoute(selectedCapability.value, row.id)
    toast.success(t('visualGameAdmin.llm.capabilities.saved'))
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

</script>

<template>
  <div class="model-capabilities vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.capabilities.title')">
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading" @click="loadCatalog">
          <RefreshCw :size="16" />
          {{ t('visualGameAdmin.llm.capabilities.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <SegmentedControl
      v-model="selectedCapability"
      class="model-capabilities__switch"
      :options="capabilityOptions"
    />

    <p v-if="error" class="vga-error">{{ error }}</p>

    <template v-if="catalog">
      <Section :title="t(`visualGameAdmin.llm.capabilities.sections.${selectedCapability}`)" :padded="false">
        <template #actions>
          <div class="model-capabilities__section-actions">
            <Select
              v-model="selectedProviderCapabilityId"
              class="model-capabilities__provider-filter"
              :options="modelProviderFilterOptions"
              :aria-label="t('visualGameAdmin.llm.capabilities.table.provider')"
              size="sm"
            />
            <Button
              variant="primary"
              size="sm"
              :disabled="!canAddModel"
              :title="canAddModel ? '' : t('visualGameAdmin.llm.capabilities.addModelUnavailable')"
              @click="openNewModel"
            >
              <Plus :size="16" />
              {{ t('visualGameAdmin.llm.capabilities.addModel') }}
            </Button>
          </div>
        </template>

        <CapabilityModelTable
          :capability="selectedCapability"
          :catalog="catalog"
          :models="currentModels"
          :saving="saving"
          @edit="openModel"
          @set-route="setRoute"
          @set-enabled="setModelEnabled"
        />
      </Section>

      <TtsVoiceCatalog
        v-if="selectedCapability === 'tts'"
        :catalog="catalog"
        @changed="catalog = $event"
      />

    </template>

    <CapabilityModelModal
      v-if="catalog"
      v-model:open="modelModalOpen"
      :capability="selectedCapability"
      :catalog="catalog"
      :model-row="editingModel"
      @saved="modelSaved"
    />
  </div>
</template>

<style scoped lang="scss">
.model-capabilities {
  display: grid;
  gap: var(--drama-space-lg);

  &__switch {
    justify-self: start;
  }

  &__provider-filter {
    min-width: 132px;
  }

  &__section-actions {
    display: flex;
    align-items: center;
    gap: var(--drama-space-sm);
  }

}
</style>
